import { FormField } from '@/components/ui/Form';
import { Form, FormInput, FormItem, FormLabel, Select } from '@/components/wm';
import { getOptionsFromJson, getStoreResource } from '@/utils';
import { apiRequest } from '@/utils/request';
import { zodResolver } from '@hookform/resolvers/zod';
import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

export function Properties({ data, onChangeField, onCompleted }) {
    const [workTypeOptions, setWorkTypeOptions] = useState([]);

    const formSchema = z.object({
        configurationId: z.string().min(1, 'Configuration is required'),
        indicatorId: z.string().min(1, 'Indicator is required'),
        name: z.string().min(1, i18next.t('form_validations.generic.name_required')),
        siteId: z.string().min(1, 'Site is required'),
        workTypeReference: z.string().min(1, 'Work type reference is required')
    });

    const form = useForm({
        resolver: zodResolver(formSchema),
        defaultValues: {
            configurationId: data.configurationId || '',
            indicatorId: data.indicatorId || '',
            name: data.name || '',
            siteId: data.siteId || '',
            workTypeReference: data.workTypeReference || ''
        },
        mode: 'onTouched'
    });

    const configurationOptions = useMemo(() => {
        const configurations = getStoreResource('integrationConfigurations') || [];

        const options = configurations.map(config => {
            const buildingName = getStoreResource('zones', config.site_id)?.name || 'Unknown Building';

            return {
                label: `${config.name} (${buildingName})`,
                value: config.id
            };
        });

        return options;
    }, []);

    const indicatorOptions = useMemo(() => {
        const indicators = getStoreResource('indicators', null, { site_id: data.siteId }) || [];
        return getOptionsFromJson(indicators);
    }, [data.siteId]);

    const GetWorkTypes = async data => {
        try {
            const payload = {
                configuration_id: data.configurationId,
                license_id: data.licenseId,
                partner_id: data.partnerId
            };

            const response = await apiRequest('/integrations/external_work_types', 'POST', payload);

            return getOptionsFromJson(response.data, 'name', 'work_type_reference');
        } catch (err) {
            console.error('Failed to fetch work type references', err);
            return [];
        }
    };

    const resetFields = (value, isFirstTime) => {
        const selectedConfig = getStoreResource('integrationConfigurations', value);

        if (onChangeField) {
            onChangeField('configurationId')(value);

            form.setValue('siteId', selectedConfig.site_id, { shouldValidate: true });
            onChangeField('siteId')(selectedConfig.site_id);

            if (isFirstTime) {
                return;
            }

            form.setValue('indicatorId', '', { shouldValidate: true });
            onChangeField('indicatorId')('');
            onChangeField('assetsAttributes')([]);
        }
    };

    useEffect(() => {
        if (data.configurationId && data.licenseId && data.partnerId) {
            GetWorkTypes(data).then(options => {
                if (options.length === 0) {
                    form.setError('workTypeReference', { message: 'No work types found for this configuration' });
                }
                setWorkTypeOptions(options);
            });
        }
    }, [data.configurationId, data.licenseId, data.partnerId]);

    useEffect(() => {
        onCompleted(form.formState.isValid);
    }, [form.formState.isValid]);

    return (
        <Form {...form}>
            <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                    <FormItem>
                        <FormLabel>{i18next.t('common.name').capitalize()}</FormLabel>
                        <FormInput
                            {...field}
                            onChange={e => {
                                const val = e.target.value;

                                field.onChange(val);
                                if (onChangeField) {
                                    onChangeField('name')(val);
                                }
                            }}
                        />
                    </FormItem>
                )}
            />
            <FormField
                control={form.control}
                name="configurationId"
                render={({ field }) => (
                    <FormItem>
                        <FormLabel>{i18next.t('glossary.configuration').capitalize()}</FormLabel>
                        <Select
                            onChange={value => {
                                const isFirstTime = !field.value;

                                field.onChange(value);
                                resetFields(value, isFirstTime);
                            }}
                            options={configurationOptions}
                            search={10}
                            value={field.value}
                        />
                    </FormItem>
                )}
            />
            <FormField
                control={form.control}
                name="indicatorId"
                render={({ field }) => (
                    <FormItem>
                        <FormLabel>{i18next.t('glossary.indicator').capitalize()}</FormLabel>
                        <Select
                            onChange={value => {
                                form.setValue('indicatorId', value, { shouldValidate: true });

                                if (onChangeField) {
                                    onChangeField('indicatorId')(value);
                                }
                            }}
                            options={indicatorOptions}
                            search={10}
                            value={field.value}
                        />
                    </FormItem>
                )}
            />
            <FormField
                control={form.control}
                name="workTypeReference"
                render={({ field }) => (
                    <FormItem>
                        <FormLabel>{i18next.t('integrations.nextbitt.work_type').capitalize()}</FormLabel>
                        <Select
                            onChange={value => {
                                field.onChange(value);
                                if (onChangeField) {
                                    onChangeField('workTypeReference')(value);
                                }
                            }}
                            options={workTypeOptions}
                            search={10}
                            value={field.value}
                        />
                    </FormItem>
                )}
            />
        </Form>
    );
}
